import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import { Container, Row, Col, InputGroup, Form, Button } from 'react-bootstrap';
import HighlightOff from 'mdi-react/HighlightOffIcon';
import Search from 'mdi-react/SearchIcon';
import { DefaultRecordType } from 'rc-table/lib/interface';

import { RootState } from 'rootReducer';
import { LogicTypes, FilterOperators, DataGrid, DataGridColumn } from 'modules/shared/DataGrid';
import ErrorMsg from 'modules/shared/ErrorMsg';
import { useGridState } from 'modules/shared/DataGrid/hooks/useGridState';
import ModalWrapper from 'modules/shared/ModalWrapper';
import { Filter, GridFilter } from 'modules/shared/DataGrid/utils/interfaces';
import classes from 'modules/security/containers/TripsTabContainer/TripsTab.module.scss';
import { StatusCell } from 'modules/shared/DataGrid/components/Cells';
import { logoutSelector } from 'modules/common/LogoutBtn/selectors';
import { useModal } from 'modules/shared/hooks';
import TripColumnsMultiSelect from 'modules/shared/TripColumnsMultiSelect';
import { TripStatusIds, tripStatusBackgroundColor, clientDateFormat } from '../../../../entities/Trip';
import { getTripsRequest, getUnloadingTripsRequest, getTripsError } from './services/actions';
import {
  AWAITING_DIRECTOR_STATUS_ID,
  MIN_FILTER_VALUE_LENGTH,
  GetTripColumnsNames,
  defaultGridState,
} from './services/constants';

type Props = {
  lolomode: number;
};

export const TripsTabContainer: React.FC<Props> = ({ lolomode }) => {
  const dispatch = useDispatch();

  const history = useHistory();
  const trips = useSelector((state: RootState) => state.directorModuleReducer.tripsTabReducer.trips);
  const error = useSelector((state: RootState) => state.directorModuleReducer.tripsTabReducer.error);
  const { branchTitle } = useSelector(logoutSelector);

  const [filterInputValue, setFilterInputValue] = useState('');
  const filterInputRef = useRef<HTMLInputElement>(null);
  const { isShow, openModal, closeModal } = useModal();

  const gridState = useGridState(trips, defaultGridState);

  useEffect(() => {
    const awaitingTrips = trips.filter((trip) => trip.statusId === AWAITING_DIRECTOR_STATUS_ID);

    if (awaitingTrips.length) openModal();
  }, []);

  setTimeout(() => {
    const awaitingTrips = trips.filter((trip) => trip.statusId === AWAITING_DIRECTOR_STATUS_ID);

    if (awaitingTrips.length) openModal();
  }, 45000);

  const awaitingDepartureLength = useMemo(() => {
    if (!trips.length) return 0;

    const awaitingTrips = trips.filter((trip) => trip.statusId === AWAITING_DIRECTOR_STATUS_ID);
    return awaitingTrips.length;
  }, [trips]);

  const [filterColumnsSelected, setFilterColumnsSelected] = useState<string[]>([]);
  useEffect(() => {
    dispatch(lolomode === 1 ? getTripsRequest() : getUnloadingTripsRequest());
    gridState.setFirstPage();
    setFilterColumnsSelected([]);
  }, [lolomode]);

  useEffect(() => {
    setTimeout(() => dispatch(lolomode === 1 ? getTripsRequest() : getUnloadingTripsRequest()), 500);
  }, [branchTitle]);

  useEffect(() => {
    const updateData = setInterval(
      () => dispatch(lolomode === 1 ? getTripsRequest() : getUnloadingTripsRequest()),
      50000,
    );

    if (error) clearInterval(updateData);
    return function cleanup() {
      clearInterval(updateData);
    };
  }, []);

  const handleFilterColumnsChange = (column: string, isChecked: boolean) => {
    if (isChecked && !filterColumnsSelected.includes(column)) {
      setFilterColumnsSelected([...filterColumnsSelected, column]);
    } else if (!isChecked && filterColumnsSelected.includes(column)) {
      setFilterColumnsSelected(filterColumnsSelected.filter((x) => x !== column));
    }
  };

  useEffect(() => {
    if (filterInputValue.length < MIN_FILTER_VALUE_LENGTH) {
      gridState.setFilter({
        logic: LogicTypes.and,
        filters: [],
      });
    } else {
      const filterColumns =
        filterColumnsSelected && filterColumnsSelected.length > 0
          ? filterColumnsSelected
          : Object.keys(GetTripColumnsNames(lolomode === 1));

      const newFilter = {
        logic: LogicTypes.or,
        filters: filterColumns.map(
          (name): Filter => ({
            operator: FilterOperators.contains,
            value: filterInputValue.trim(),
            field: name,
          }),
        ),
      } as GridFilter;
      gridState.setFilter(newFilter);
    }
  }, [filterInputValue, filterColumnsSelected]);

  const handleFilterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFilterInputValue(e.target.value);
  };

  const handleClearIconClick = () => {
    setFilterInputValue('');
    if (filterInputRef.current) {
      filterInputRef.current.focus();
    }
  };

  const addRowColorByStatus = (rowData: DefaultRecordType) => {
    const { statusId, id } = rowData;
    return {
      onDoubleClick: () => history.push(`/director/trip/${id}`),
      style: { backgroundColor: tripStatusBackgroundColor.director['Порядок 1'][statusId as TripStatusIds] },
    };
  };

  const factoryColumnName = lolomode === 1 ? 'factoryNumber' : 'unloadingFactory';
  const warehouseColumnName = lolomode === 1 ? 'warehouseCode' : 'unloadingWarehouse';

  return (
    <React.Fragment>
      <Container fluid className={classes.filterContainer}>
        <Row>
          <Col className="d-flex align-items-center">
            <InputGroup className={classes.filter}>
              <Form.Control
                as="input"
                type="text"
                name="filter"
                onChange={handleFilterChange}
                value={filterInputValue}
                ref={filterInputRef}
              />
              <Search size={18} className={classes.searchIcon} />
              {!!filterInputValue && (
                <HighlightOff size={18} className={classes.clearFilterIcon} onClick={handleClearIconClick} />
              )}
            </InputGroup>
            <TripColumnsMultiSelect
              onFilterColumnsChange={handleFilterColumnsChange}
              columnNames={Object.entries(GetTripColumnsNames(lolomode === 1))}
              initSelectedColumns={filterColumnsSelected}
            />

            {!!trips.length && <span className="ml-2">Всього: {trips.length}</span>}
            {!!awaitingDepartureLength && <span className="ml-2">Очікують завсклада: {awaitingDepartureLength}</span>}
          </Col>
        </Row>
      </Container>
      <DataGrid
        pageable
        data={gridState.gridData}
        sort={gridState.sort}
        total={gridState.total}
        skip={gridState.pagination?.skip}
        pageSize={gridState.pagination?.pageSize}
        onPageChange={gridState.handlePageChange}
        onSortChange={gridState.handleSortChange}
        onRow={addRowColorByStatus}
      >
        <DataGridColumn title="Рейс" dataIndex="id" key="id" width="5%" />
        <DataGridColumn
          title="Дата бронювання"
          dataIndex="bookingDate"
          key="arrivalDate"
          dateFormat={clientDateFormat}
          width="12%"
          className={classes.bookingDate}
        />
        <DataGridColumn title="Статус" dataIndex="statusId" key="statusId" render={StatusCell} width="14%" sortable />
        <DataGridColumn hidden dataIndex="statusStr" key="statusStr" />
        <DataGridColumn
          title="В'їзд"
          dataIndex="arrivalDate"
          key="arrivalDate"
          dateFormat={clientDateFormat}
          width="12%"
          sortable
        />
        <DataGridColumn title="Тип перевізника" dataIndex="сarrierTypeStr" key="driver" width="8%" />
        <DataGridColumn title="Держномер ТЗ" dataIndex="vehicleLicensePlate" key="vehicleLicensePlate" width="10%" />
        <DataGridColumn
          title="Держномер причепа"
          dataIndex="trailerLicensePlate"
          key="trailerLicensePlate"
          width="14%"
        />
        <DataGridColumn title="Водій" dataIndex="driver" key="driver" width="12%" />
        <DataGridColumn title="Телефон" dataIndex="driverPhone" key="driverPhone" width="9%" />
        <DataGridColumn title="Експедитор" dataIndex="forwarder" key="forwarder" width="12%" />
        <DataGridColumn title="Телефон" dataIndex="forwarderPhone" key="forwarderPhone" width="12%" />
        {lolomode === 1 && <DataGridColumn title="Номер Наряда" dataIndex="orders" key="orders" width="10%" />}
        {lolomode === 1 && (
          <DataGridColumn
            title="Фактична вага,т (ТСД)"
            dataIndex="factWeightGrouped"
            key="factWeightGrouped"
            width="10%"
          />
        )}
        {lolomode === 2 && (
          <DataGridColumn title="Локація" dataIndex="unloadingLocation" key="unloadingLocation" width="10%" />
        )}
        <DataGridColumn title="Номер Заводу" dataIndex={factoryColumnName} key={factoryColumnName} width="5%" />{' '}
        <DataGridColumn title="Номер Складу" dataIndex={warehouseColumnName} key={warehouseColumnName} width="5%" />
        <DataGridColumn
          title="Кількість позицій у наряді"
          dataIndex="orderItemsCount"
          key="orderItemsCount"
          width="5%"
        />
      </DataGrid>
      <ErrorMsg
        isShow={!!error}
        error={error?.message || 'Помилка при отриманні данних'}
        closeHandler={() => dispatch(getTripsError(null))}
      />
      <ModalWrapper show={isShow} onHide={closeModal}>
        <div>
          <h3 className="text-center text-danger mt-0 mb-1">Виклик!</h3>
          <h5 className="text-center">Вас очікують на КПП</h5>
          <div className="d-flex justify-content-center my-3">
            <Button className="px-5" variant="outline-secondary" onClick={closeModal}>
              Ok
            </Button>
          </div>
        </div>
      </ModalWrapper>
    </React.Fragment>
  );
};
